import type { Schema } from '../api/client';
import { audioDirection } from './dialogueMode';
import type { ArtDesign } from './ShotPlan';
type Shot = Schema<'ProductionShot'>;
// The saved frame intervals still drive editing and captions. Video directions describe
// the same performance in order, keeping each spoken line beside its action.
function performance(shot: Shot): string[] {
  const lines = shot.dialogueLines ?? [];
  const out = [
    lines.length
      ? '按下列顺序表演，完整说出引号内台词。仅当前画内说话者做口型，其他人闭口聆听；旁白不带动画内人物口型，不加台词。'
      : '声音：只生成同步环境和动作声，不添加对白、旁白或人声音乐。',
  ];
  const beats = shot.actionBeats ?? [];
  beats.forEach((beat, i) => {
    out.push(
      `${i === 0 ? '先' : i === beats.length - 1 ? '最后' : '随后'}：${beat.action}${beat.sound ? '；现场声：' + beat.sound : ''}`,
    );
    for (const line of lines) {
      if (line.startFrame < beat.startFrame && line.endFrame > beat.startFrame)
        out.push(`此时${line.speaker}承接上一段说完原句，不重说。`);
    }
    for (const line of lines) {
      if (line.startFrame < beat.startFrame || line.startFrame >= beat.endFrame) continue;
      const target =
        line.mode === 'narration'
          ? '在画外讲述'
          : line.addressMode === 'self'
            ? '自言自语'
            : `对${line.addressees.map((r) => r.name).join('、')}说`;
      out.push(
        `${line.startFrame === beat.startFrame ? '同时，' : '本段中，'}${line.speaker}${target}；视线：${line.gaze}${line.delivery ? '；语气：' + line.delivery : ''}：“${line.text}”${line.listenerReaction ? '；听者反应：' + line.listenerReaction : ''}${line.endFrame > beat.endFrame ? '；这句连续说到下一动作段' : ''}。`,
      );
    }
  });
  out.push('收尾保持最后一段的结束状态，不新增动作或转场。');
  return out;
}
export function syncShot(shot: Shot): Shot {
  if (!shot.dialogueLines) return shot;
  const speakers = [...new Set(shot.dialogueLines.map((l) => l.speaker))];
  return {
    ...shot,
    dialogue: shot.dialogueLines.map((l) => l.text).join('\n'),
    speaker: speakers.length === 1 ? speakers[0]! : '',
    dialogueMode: shot.dialogueLines[0]?.mode ?? 'dialogue',
  };
}
export function shotPrompt(
  shot: Shot,
  style: string,
  video: boolean,
  fps = 24,
  design: ArtDesign = {},
): string {
  if (!shot.staging)
    return `${style}\n${shot.description}\n竖屏构图9:16，不要字幕或水印。${video ? audioDirection(shot, fps) : ''}`;
  const stage = shot.staging,
    visible = stage.blocking.filter((r) => r.presentAtStart);
  const out = [
    video
      ? '9:16连续短剧视频，保持人物与场景连续。'
      : '生成一张9:16起始静帧，只表现开场瞬间，不拼贴、不表现后续动作。',
    `场景：${stage.scene}`,
    `景别与构图：${stage.framing}`,
    `机位：${stage.camera}`,
    `光线：${stage.lighting}`,
    `开场瞬间：${stage.opening}`,
    `开场必须看见${visible.length}个独立人物，不合并、不漏画：`,
    ...visible.map(
      (r) => `${r.name}：${r.position}；${r.pose}；${r.expression}；视线：${r.gaze}。`,
    ),
    ...stage.blocking.filter((r) => !r.presentAtStart).map((r) => `${r.name}开场不在画内。`),
    `人物外观以各自定妆图为准，视线看向剧情对象，不看摄影机或观众。画风：${style}`,
    ...stage.continuityNotes.map((n) => `本镜连续性：${n}`),
  ];
  for (const scene of design.sceneDesigns ?? [])
    if (scene.id === stage.sceneId)
      out.push(
        `共享场景：${scene.period}；${scene.layout}；${scene.details}；光源：${scene.lighting}。`,
      );
  for (const prop of stage.props) {
    const common = design.propDesigns?.find((d) => d.id === prop.propId);
    if (common)
      out.push(
        `道具设计·${common.name}：${common.material}；${common.scale}；${common.appearance}；本镜位置：${prop.position}；当前状态：${prop.state}`,
      );
  }
  if (video) {
    out.push(...performance(shot));
  }
  out.push('画面内不加字幕、对白文字或水印。');
  return out.join('\n');
}
